import {
  configureApi,
  setAuth0PasswordResetConfig,
  setE2EUserHeaderGetter,
  setErrorReporter,
  setImpersonatedUserIdGetter,
  setNotifier,
} from '@doneverse/data/api'
import { trace, SpanStatusCode } from '@opentelemetry/api'
import { PostHogProvider } from '@posthog/react'
import * as Sentry from '@sentry/react'
import { createRoot } from 'react-dom/client'
import { toast } from 'sonner'
import App from './App.tsx'
import './index.css'
import { DONEVERSE_ENV, DONEVERSE_RELEASE, OBSERVABILITY_ENABLED } from './lib/env'
import { getImpersonatedUser } from './lib/impersonation-storage'
import { initObservability } from './lib/observability'
import { initPostHog, posthog } from './lib/posthog'
import { initViewportHeight } from './lib/viewport'

// Wire @doneverse/data into web-specific providers (must run before any
// query/service call). The data package itself stays platform-agnostic; the
// dashboard injects its sonner notifier, Vite env config, and localStorage
// readers here.
configureApi({
  baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3001',
  e2e: import.meta.env.VITE_E2E_TEST_MODE === 'true',
})
setNotifier({
  success: (message) => toast.success(message),
  error: (message) => toast.error(message),
  info: (message) => toast.info(message),
})
setE2EUserHeaderGetter(() => localStorage.getItem('e2e_test_user'))
setImpersonatedUserIdGetter(() => getImpersonatedUser()?.id ?? null)

// In e2e mode neither the AI Coaching page nor the founder intake can reach a
// real ElevenLabs agent, so inject the in-memory mock for both. Dynamic +
// E2E-only so the mock provider stays out of normal production builds (it's
// test infra, never shipped to real users).
if (import.meta.env.VITE_E2E_TEST_MODE === 'true') {
  void Promise.all([import('@/lib/conversation/override'), import('@/lib/conversation/mockProvider')]).then(
    ([
      { setDoerCoachProviderOverride, setOnboardingIntakeProviderOverride },
      { MockConversationProvider, e2eCoachReply },
    ]) => {
      // The "close session <uuid>" / "emit <json>" spec seams live in
      // e2eCoachReply, next to the mock provider. One factory, invoked per page
      // mount, so each surface still gets its own instance — they share a fake
      // only for as long as neither needs agent-specific behaviour.
      const mockProvider = () => new MockConversationProvider({ reply: e2eCoachReply, replyDelayMs: 1_000 })
      setDoerCoachProviderOverride(mockProvider)
      setOnboardingIntakeProviderOverride(mockProvider)
    },
  )
}
setAuth0PasswordResetConfig({
  domain: import.meta.env.VITE_AUTH0_DOMAIN as string,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID as string,
  connection: import.meta.env.VITE_AUTH0_CONNECTION as string | undefined,
})

// Initialize observability (must be before app renders)
initObservability()
initPostHog()

// Track the visual viewport so the app shell shrinks when the mobile keyboard
// opens, keeping the sticky header on screen (see lib/viewport.ts).
initViewportHeight()

// A failed lazy-chunk fetch (tab open across a deploy, edge blip) otherwise
// leaves the navigation dead until the user manually reloads. Reload to pick
// up the current build; the guard stops a reload loop if the chunk stays
// unreachable, letting the error surface to Sentry instead.
window.addEventListener('vite:preloadError', (event) => {
  const lastReload = Number(sessionStorage.getItem('chunk-reload-at') ?? 0)
  if (Date.now() - lastReload < 30_000) return
  event.preventDefault()
  sessionStorage.setItem('chunk-reload-at', String(Date.now()))
  window.location.reload()
})

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: DONEVERSE_ENV,
  // Pins each error to a build so "new since the last deploy" is answerable.
  // Left unset for unstamped builds so they stay releaseless.
  ...(DONEVERSE_RELEASE ? { release: DONEVERSE_RELEASE } : {}),
  enabled: OBSERVABILITY_ENABLED && !!import.meta.env.VITE_SENTRY_DSN,
  tracesSampleRate: 0, // Using OpenTelemetry for tracing
})

setErrorReporter((error, ctx) => {
  const err = error instanceof Error ? error : new Error(String(error))
  const tracer = trace.getTracer('doneverse-dashboard')
  const span = tracer.startSpan(`${ctx.source}.error`)
  span.setAttribute('error.source', ctx.source)
  if (ctx.status != null) span.setAttribute('http.status_code', ctx.status)
  if (ctx.key !== undefined) span.setAttribute('error.key', JSON.stringify(ctx.key))
  span.recordException(err)
  span.setStatus({ code: SpanStatusCode.ERROR, message: err.message })
  span.end()
  Sentry.captureException(err, { extra: { source: ctx.source, key: ctx.key, status: ctx.status } })
})

createRoot(document.getElementById('root')!).render(
  <Sentry.ErrorBoundary fallback={<div>Something went wrong.</div>}>
    <PostHogProvider client={posthog}>
      <App />
    </PostHogProvider>
  </Sentry.ErrorBoundary>,
)
